import * as THREE from 'three';
import { BLACK, basic, emis, std } from '../materials.ts';
import { G, M, Ribbon, V, glowSprite, wisp } from '../prims.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Escarabajo de tinta (docs/CRIATURAS.md §2): the era II armoured nightmare. A beetle the size of a pony, low and
 * wide, under a domed carapace of polished ink: two ridged wing-cases that throw a violet and sea-green sheen
 * along their striae, a shield over the thorax with a great horn sweeping forward over the head, a shorter horn
 * rising from the head to meet it, serrated mandibles and six spurred legs. Acid wells green in the glands along
 * its flanks and in its mouth. With `cracked`, a heavy blow has split the shell: the left wing-case broke off at
 * the waist, the right one split down its length and hangs open, and the soft pale-violet body shows through,
 * seamed with acid light.
 *
 * Opaque shared materials; the acid is its one self-lit material and the ink smoke its one translucent one, plus
 * glow sprites.
 */

const UP = V(0, 1, 0);
/** Uniform size of the miniature against its modelled coordinates. */
const SIZE = 0.78;

/** Each leg's hip, knee and foot (right side; the left mirrors x), front to back. */
const LEG: readonly (readonly [THREE.Vector3, THREE.Vector3, THREE.Vector3])[] = [
  [V(0.26, 0.32, 0.56), V(0.56, 0.48, 0.82), V(0.66, 0.07, 1.06)],
  [V(0.32, 0.3, 0.12), V(0.7, 0.48, 0.16), V(0.88, 0.07, 0.1)],
  [V(0.3, 0.3, -0.24), V(0.66, 0.46, -0.5), V(0.84, 0.07, -0.84)],
];
/** Leg order (bones leg0…leg5): right front, middle, rear; then left front, middle, rear. */
const legOf = (i: number): { side: number; row: number } => ({ side: i < 3 ? 1 : -1, row: i % 3 });

/** A bone turned about Y so its local +Z runs out along the leg (its X turns lift and lower the leg). */
const yjoint = (parentAbs: THREE.Object3D, at: THREE.Vector3, yaw: number, name: string): { p: THREE.Group; abs: THREE.Group } => {
  const p = new THREE.Group();
  p.name = name;
  p.position.copy(at);
  p.rotation.y = yaw;
  parentAbs.add(p);
  const abs = new THREE.Group();
  abs.rotation.y = -yaw;
  abs.position.copy(at).negate().applyAxisAngle(UP, -yaw);
  p.add(abs);
  return { p, abs };
};

/** A tapering tube along a curve (horns, mandibles, leg segments), closed at the tip. */
const taper = (pts: THREE.Vector3[], r0: number, r1: number, seg = 6, rad = 6, flat = 1): THREE.BufferGeometry => {
  const curve = new THREE.CatmullRomCurve3(pts);
  const fr = curve.computeFrenetFrames(seg, false);
  const pos: number[] = [];
  const idx: number[] = [];
  for (let i = 0; i <= seg; i++) {
    const k = i / seg;
    const c = curve.getPointAt(k);
    const r = r0 + (r1 - r0) * k;
    const n = fr.normals[i] as THREE.Vector3, b = fr.binormals[i] as THREE.Vector3;
    for (let j = 0; j < rad; j++) {
      const a = (j / rad) * Math.PI * 2;
      const p = c.clone().addScaledVector(n, Math.cos(a) * r * flat).addScaledVector(b, Math.sin(a) * r);
      pos.push(p.x, p.y, p.z);
    }
  }
  const tip = curve.getPointAt(1).addScaledVector(fr.tangents[seg] as THREE.Vector3, r1 * 1.5);
  pos.push(tip.x, tip.y, tip.z);
  const ti = (seg + 1) * rad;
  for (let i = 0; i < seg; i++) {
    for (let j = 0; j < rad; j++) {
      const a = i * rad + j, b2 = i * rad + ((j + 1) % rad), c = a + rad, d = b2 + rad;
      idx.push(a, c, b2, b2, c, d);
    }
  }
  for (let j = 0; j < rad; j++) idx.push(seg * rad + j, ti, seg * rad + ((j + 1) % rad));
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
};

type Surf = (u: number, v: number) => THREE.Vector3;

/**
 * A plate with thickness from a surface `P(u, v)` over a nu × nv grid: the outer skin goes to the material
 * `paint(i, j)` picks for each cell (so sheen can run in bands with smooth shading across them), the inner skin
 * `thick` under it and walls round the edge. Where a kept cell meets a dropped one (`keep`) the plate is broken:
 * the vertices along the break are jittered (`jag`, from `R`) so it runs jagged, and its wall is `seam` (acid
 * welling in the crack).
 */
const slab = (parent: THREE.Object3D, P: Surf, nu: number, nv: number, thick: number, paint: (i: number, j: number) => THREE.Material, under: THREE.Material, keep: (i: number, j: number) => boolean = () => true, center = V(0, 0.4, -0.3), seam = under, R: () => number = () => 0, jag = 0): void => {
  const pt: THREE.Vector3[][] = [];
  const nm: THREE.Vector3[][] = [];
  const inside = (i: number, j: number): boolean => i >= 0 && j >= 0 && i < nu && j < nv;
  /** A vertex on a break: among the cells round it, some kept and some dropped. */
  const onBreak = (i: number, j: number): boolean => {
    let k = 0, d = 0;
    for (const [a, b] of [[i - 1, j - 1], [i, j - 1], [i - 1, j], [i, j]] as const) {
      if (!inside(a, b)) continue;
      if (keep(a, b)) k++;
      else d++;
    }
    return k > 0 && d > 0;
  };
  for (let i = 0; i <= nu; i++) {
    pt.push([]);
    for (let j = 0; j <= nv; j++) {
      const b = jag > 0 && onBreak(i, j);
      const u = Math.min(1, Math.max(0, (i + (b ? R() * jag * 2 : 0)) / nu));
      const v = Math.min(1, Math.max(0, (j + (b ? R() * jag * 2 : 0)) / nv));
      (pt[i] as THREE.Vector3[]).push(P(u, v));
    }
  }
  const at = (i: number, j: number): THREE.Vector3 => (pt[Math.max(0, Math.min(nu, i))] as THREE.Vector3[])[Math.max(0, Math.min(nv, j))] as THREE.Vector3;
  for (let i = 0; i <= nu; i++) {
    nm.push([]);
    for (let j = 0; j <= nv; j++) {
      const du = at(i + 1, j).clone().sub(at(i - 1, j));
      const dv = at(i, j + 1).clone().sub(at(i, j - 1));
      const n = du.cross(dv).normalize();
      if (n.dot(at(i, j).clone().sub(center)) < 0) n.negate();
      (nm[i] as THREE.Vector3[]).push(n);
    }
  }
  const O = (i: number, j: number): THREE.Vector3 => (pt[i] as THREE.Vector3[])[j] as THREE.Vector3;
  const N = (i: number, j: number): THREE.Vector3 => (nm[i] as THREE.Vector3[])[j] as THREE.Vector3;
  const I = (i: number, j: number): THREE.Vector3 => O(i, j).clone().addScaledVector(N(i, j), -thick);
  const buckets = new Map<THREE.Material, { p: number[]; n: number[] }>();
  const put = (m: THREE.Material, vs: THREE.Vector3[], ns: THREE.Vector3[] | THREE.Vector3, want: THREE.Vector3): void => {
    const b = buckets.get(m) ?? { p: [], n: [] };
    buckets.set(m, b);
    const [a, c, d] = vs as [THREE.Vector3, THREE.Vector3, THREE.Vector3];
    const f = c.clone().sub(a).cross(d.clone().sub(a));
    const order = f.dot(want) >= 0 ? [0, 1, 2] : [0, 2, 1];
    for (const k of order) {
      const v = vs[k] as THREE.Vector3;
      const n = Array.isArray(ns) ? (ns[k] as THREE.Vector3) : ns;
      b.p.push(v.x, v.y, v.z);
      b.n.push(n.x, n.y, n.z);
    }
  };
  const kept = (i: number, j: number): boolean => inside(i, j) && keep(i, j);
  for (let i = 0; i < nu; i++) {
    for (let j = 0; j < nv; j++) {
      if (!keep(i, j)) continue;
      const mat = paint(i, j);
      const q: [number, number][] = [[i, j], [i + 1, j], [i + 1, j + 1], [i, j + 1]];
      const want = N(i, j).clone().add(N(i + 1, j + 1));
      const ov = q.map(([a, b]) => O(a, b)), on = q.map(([a, b]) => N(a, b));
      put(mat, [ov[0]!, ov[1]!, ov[2]!], [on[0]!, on[1]!, on[2]!], want);
      put(mat, [ov[0]!, ov[2]!, ov[3]!], [on[0]!, on[2]!, on[3]!], want);
      const iv = q.map(([a, b]) => I(a, b)), inn = on.map((n) => n.clone().negate());
      const back = want.clone().negate();
      put(under, [iv[0]!, iv[1]!, iv[2]!], [inn[0]!, inn[1]!, inn[2]!], back);
      put(under, [iv[0]!, iv[2]!, iv[3]!], [inn[0]!, inn[2]!, inn[3]!], back);
      // Walls along each open side.
      const mid = ov.reduce((s, v) => s.add(v), V(0, 0, 0)).multiplyScalar(0.25);
      const sides: [number, number, number, number, number, number][] = [[i, j, i + 1, j, i, j - 1], [i + 1, j, i + 1, j + 1, i + 1, j], [i + 1, j + 1, i, j + 1, i, j + 1], [i, j + 1, i, j, i - 1, j]];
      for (const [a1, b1, a2, b2, ni, nj] of sides) {
        if (kept(ni, nj)) continue;
        const p1 = O(a1, b1), p2 = O(a2, b2), p3 = I(a2, b2), p4 = I(a1, b1);
        const out = p1.clone().add(p2).multiplyScalar(0.5).sub(mid);
        const wn = p2.clone().sub(p1).cross(p4.clone().sub(p1)).normalize();
        if (wn.dot(out) < 0) wn.negate();
        const wm = inside(ni, nj) ? seam : under;
        put(wm, [p1, p2, p3], wn, out);
        put(wm, [p1, p3, p4], wn, out);
      }
    }
  }
  for (const [m, b] of buckets) {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(b.p, 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute(b.n, 3));
    M(g, m, parent);
  }
};

/**
 * Bones: `body` (thorax and abdomen, pivoting over the middle legs) carries the thorax shield and its horn, the
 * `abdomen` (it breathes), the wing-cases `shellL` and `shellR` (hinged at the shoulders by the midline: −X lifts
 * their rear, ±Z swings them out), the `head` with its `mandL`/`mandR` (±Y opens them) and antennae `antL`/`antR`,
 * the acid `spit` in the mouth (hidden at rest inside it; the attack scales it up and throws it) and six legs, each
 * a `leg<i>` at the hip and a `knee<i>`, turned so local +Z runs out along the leg: −X lifts it, a Y turn swings
 * it fore and aft, +X on the knee tucks the foot in. Legs 0–2 are the right side front to back, 3–5 the left.
 */
export const beetle = (o?: BaseOpts & { cracked?: boolean }): Model => {
  const c = begin(o, 0.95, 53);
  const { g, R, rng } = c;
  const cracked = o?.cracked ?? false;

  // Polished ink: a black-violet chitin, glossy, with the sheen of oil on water along the ridges.
  const ink = std('#140f20', { roughness: 0.2, metalness: 0.5, bumpScale: 0.006 });
  const sheenV = std('#1e1638', { roughness: 0.18, metalness: 0.55, bumpScale: 0.006 });
  const sheenT = std('#163a48', { roughness: 0.18, metalness: 0.55, bumpScale: 0.006 });
  const sheenM = std('#281532', { roughness: 0.2, metalness: 0.55, bumpScale: 0.006 });
  const dull = std('#0e0c12', { roughness: 0.55, metalness: 0.2, bumpScale: 0.02 });
  const inner = std('#241a30', { roughness: 0.7, bumpScale: 0.04, flatShading: true });
  const flesh = std('#8c78b4', { roughness: 0.42, bumpScale: 0.035 });
  const fleshD = std('#6c5684', { roughness: 0.5, bumpScale: 0.04 });
  const tooth = std('#b9b2bf', { roughness: 0.45 });
  const acid = emis('#d4ff7a', '#8cff2e', 1.6);
  const ACID = '#9dff3a';

  // Modelled long (horn to tail ~2.4); shrunk to sit in the same frame as the other nightmares (see library scale).
  const sc = G(g);
  sc.scale.setScalar(SIZE);
  const bodyJ = joint(sc, V(0, 0.45, 0.1), 'body');
  const bd = bodyJ.abs;
  const abdJ = joint(bd, V(0, 0.46, 0.3), 'abdomen');
  const headJ = joint(bd, V(0, 0.48, 0.8), 'head');
  const hd = headJ.abs;
  const mandJ = [-1, 1].map((k) => joint(hd, V(k * 0.13, 0.38, 1.04), k < 0 ? 'mandL' : 'mandR'));
  const antJ = [-1, 1].map((k) => joint(hd, V(k * 0.17, 0.52, 1.02), k < 0 ? 'antL' : 'antR'));
  const spitJ = joint(hd, V(0, 0.4, 1.06), 'spit');
  const shellJ = [-1, 1].map((k) => joint(bd, V(k * 0.05, 0.86, 0.36), k < 0 ? 'shellL' : 'shellR'));

  // ---------- Wing-cases ----------
  /** The right wing-case's surface (k = −1 mirrors it): u from the midline seam down past the flank, v from shoulder to tail. */
  const ely = (k: number): Surf => (u, v0) => {
    // Rows bunch toward the tail so it rounds off instead of ending in a flat cap.
    const v = Math.sin((v0 * Math.PI) / 2);
    const s = Math.sqrt(Math.max(0, 1 - v ** 2.6)) * (0.9 + 0.1 * Math.min(1, v * 4));
    const a = 0.02 + u * 1.95;
    // Striae: every other column raised, and a heavy keel near the flank.
    const col = Math.round(u * NU);
    const rib = (col % 2 === 1 ? 0.018 : 0) + 0.025 * Math.exp(-(((u - 0.625) / 0.07) ** 2));
    const A = 0.62 + rib, B = 0.5 + rib;
    const z = 0.38 - v * 1.36;
    // The lip tucks under at the flank and the rear end curls down to the tail.
    return V(k * (0.012 + A * s * Math.sin(a)), 0.44 + B * s * Math.cos(a) - 0.06 * v * v, z);
  };
  /**
   * Sheen along the striae, shifting with the angle the way oil on water does: the ridges over the top catch violet,
   * the keel plum, the ridges down the flank sea-green; ink in the grooves between.
   */
  const elyPaint = (i: number): THREE.Material => (i % 2 === 0 ? ink : i < 4 ? sheenV : i < 6 ? sheenM : sheenT);
  const NU = 8, NV = 9;
  if (!cracked) {
    for (const [i, k] of [[0, -1], [1, 1]] as const) slab(shellJ[i]!.abs, ely(k), NU, NV, 0.035, elyPaint, inner);
  } else {
    // Left: broken off across the waist, a jagged edge; the rear half is gone.
    const brk = [4, 3, 3, 2, 3, 3, 4, 4];
    const C0 = V(0, 0.4, -0.3);
    slab(shellJ[0]!.abs, ely(-1), NU, NV, 0.035, elyPaint, inner, (i, j) => j < (brk[i] ?? 5), C0, acid, R, 0.45);
    // Right: split down its length; the flank half sags open on a hinge of torn chitin.
    const split = [3, 2, 2, 2, 2, 3, 3, 3, 3];
    slab(shellJ[1]!.abs, ely(1), NU, NV, 0.035, elyPaint, inner, (i, j) => i < (split[j] ?? 3) && !(j > 6 && i > 1), C0, acid, R, 0.4);
    const hinge = V(0.34, 0.86, 0);
    const flap = G(shellJ[1]!.abs, hinge.x, hinge.y, hinge.z);
    flap.rotation.set(0.08, 0, -0.8);
    const flapIn = G(flap, -hinge.x, -hinge.y, -hinge.z);
    slab(flapIn, ely(1), NU, NV, 0.035, elyPaint, inner, (i, j) => i > (split[j] ?? 3) && j < 7 && !(j > 5 && i < 6), C0, acid, R, 0.4);
  }

  // ---------- Thorax shield (pronotum) and its great horn ----------
  const pro: Surf = (u, v) => {
    const a = (u - 0.5) * 3.5;
    const w = 0.4 + 0.18 * v;
    const h = 0.22 + 0.12 * v;
    const z = 0.84 - v * 0.5;
    return V(w * Math.sin(a), 0.48 + h * Math.cos(a) + 0.08 * v, z - 0.06 * Math.cos(a) * (1 - v));
  };
  slab(bd, pro, 8, 4, 0.04, (i, j) => (j === 3 ? sheenV : i === 3 || i === 4 ? sheenM : ink), inner, () => true, V(0, 0.45, 0.55));
  // The horn: out of the shield, sweeping forward over the head and curving down to a forked point.
  M(taper([V(0, 0.74, 0.62), V(0, 0.92, 0.84), V(0, 0.98, 1.08), V(0, 0.9, 1.32), V(0, 0.78, 1.42)], 0.1, 0.022, 8, 6, 0.75), ink, bd);
  for (const k of [-1, 1]) M(taper([V(0, 0.8, 1.4), V(k * 0.05, 0.72, 1.46), V(k * 0.07, 0.66, 1.46)], 0.022, 0.006, 3, 4), sheenV, bd);
  // Teeth along its underside, and the shield's shoulders spurred.
  for (let t = 0; t < 3; t++) {
    const tt = M(new THREE.ConeGeometry(0.02, 0.07, 4), ink, bd);
    tt.position.set(0, 0.86 - t * 0.02, 1.0 + t * 0.12);
    tt.rotation.x = Math.PI + 0.3;
  }
  for (const k of [-1, 1]) {
    M(taper([V(k * 0.42, 0.62, 0.48), V(k * 0.54, 0.66, 0.62), V(k * 0.58, 0.68, 0.76)], 0.05, 0.01, 3, 5), ink, bd);
  }

  // ---------- Abdomen: segmented, under the wing-cases (soft and pale where the shell broke) ----------
  const ab = abdJ.abs;
  const SEG = 5;
  const prof: THREE.Vector2[] = [];
  for (let i = 0; i <= 10; i++) {
    const v = i / 10;
    const bulge = 1 - 0.1 * Math.abs(Math.cos(v * SEG * Math.PI));
    const r = 0.5 * Math.sin(Math.min(Math.PI, 0.25 + v * (Math.PI - 0.25))) ** 0.6 * bulge;
    prof.push(new THREE.Vector2(Math.max(0.001, r), -v * 1.32));
  }
  // Two halves: the back (soft flesh once the shell is gone) and the plated belly.
  for (const top of [true, false]) {
    const lg = new THREE.LatheGeometry(prof, 5, top ? -Math.PI / 2 : Math.PI / 2, Math.PI);
    lg.rotateX(Math.PI / 2);
    const half = M(lg, top && cracked ? flesh : dull, ab);
    half.position.set(0, 0.47, 0.32);
    half.scale.set(1.0, 0.62, 1);
  }
  if (cracked) {
    // Acid seams between the segments, across the exposed back, and a vein or two wandering through them.
    for (let s = 1; s < SEG; s++) {
      const z = 0.32 - (s / SEG) * 1.32;
      const r = 0.5 * Math.sin(0.25 + (s / SEG) * (Math.PI - 0.25)) ** 0.6 * 0.9;
      const pts: THREE.Vector3[] = [];
      for (let a = -1.25; a <= 1.26; a += 0.42) pts.push(V(Math.sin(a) * r * 1.01, 0.47 + Math.cos(a) * r * 0.63, z + R() * 0.02));
      M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 6, 0.013, 3), acid, ab);
    }
    for (const [x, z] of [[0.12, -0.1], [-0.15, -0.5], [0.2, -0.6]] as const) {
      const lump = M(new THREE.SphereGeometry(0.09, 6, 4), fleshD, ab);
      lump.position.set(x, 0.72 - Math.abs(z + 0.3) * 0.12, z);
      lump.scale.set(1.2, 0.5, 1.4);
    }
    const veins: THREE.Vector3[][] = [[V(-0.05, 0.77, 0.12), V(0.08, 0.77, -0.15), V(0.02, 0.76, -0.4), V(0.15, 0.73, -0.65)], [V(-0.25, 0.73, -0.1), V(-0.2, 0.75, -0.35), V(-0.3, 0.7, -0.6)]];
    for (const p of veins) M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(p), 6, 0.009, 3), acid, ab);
    glowSprite(ab, ACID, 1.1, V(0, 0.82, -0.3), 0.3);
  }
  // Underside: the plates of the thorax between the legs.
  const ster = M(new THREE.SphereGeometry(0.34, 8, 4), dull, bd);
  ster.position.set(0, 0.38, 0.42);
  ster.scale.set(1, 0.5, 1.25);

  // Acid glands: a row of swollen pores down each flank, under the lip of the shell.
  for (const k of [-1, 1]) {
    for (let p = 0; p < 3; p++) {
      const gl = M(new THREE.SphereGeometry(0.038, 5, 3), acid, ab);
      gl.position.set(k * 0.52, 0.38, 0.08 - p * 0.32);
      gl.scale.set(0.6, 1, 1.2);
    }
    glowSprite(ab, ACID, 0.35, V(k * 0.55, 0.44, -0.25), 0.4);
  }

  // ---------- Head ----------
  const skull = M(new THREE.SphereGeometry(0.25, 9, 6), ink, hd);
  skull.position.set(0, 0.48, 0.9);
  skull.scale.set(1.2, 0.72, 1.0);
  const clyp = M(new THREE.SphereGeometry(0.17, 8, 4, 0, Math.PI * 2, 0, Math.PI / 2), sheenV, hd);
  clyp.position.set(0, 0.52, 1.02);
  clyp.scale.set(1.1, 0.5, 1.0);
  clyp.rotation.x = 0.5;
  // The head horn: short and thick, curving up to meet the great horn above it like a pincer.
  M(taper([V(0, 0.56, 1.08), V(0, 0.66, 1.22), V(0, 0.8, 1.27), V(0, 0.9, 1.22)], 0.065, 0.014, 6, 6, 0.8), ink, hd);
  // Eyes: glossy domes on the sides with a pin of acid behind them.
  for (const k of [-1, 1]) {
    const eye = M(new THREE.SphereGeometry(0.055, 6, 4), BLACK, hd);
    eye.position.set(k * 0.24, 0.5, 0.98);
    eye.scale.set(0.7, 1, 1.1);
    const pin = M(new THREE.SphereGeometry(0.02, 5, 3), acid, hd);
    pin.position.set(k * 0.27, 0.51, 1.0);
    glowSprite(hd, ACID, 0.14, V(k * 0.28, 0.51, 1.01), 0.85);
  }
  // Mouth: a green glow in the throat, palps and a drool of acid.
  glowSprite(hd, ACID, 0.3, V(0, 0.36, 1.12), 0.35);
  for (const k of [-1, 1]) M(taper([V(k * 0.06, 0.36, 1.08), V(k * 0.08, 0.3, 1.16), V(k * 0.07, 0.24, 1.18)], 0.016, 0.008, 3, 4), dull, hd);
  M(taper([V(0, 0.36, 1.1), V(0.01, 0.28, 1.13), V(0, 0.2, 1.12)], 0.012, 0.006, 3, 4), acid, hd);

  // Mandibles: serrated sickles hooking in to meet under the horns.
  for (const k of [-1, 1]) {
    const mj = mandJ[k < 0 ? 0 : 1]!.abs;
    M(taper([V(k * 0.13, 0.38, 1.04), V(k * 0.2, 0.37, 1.18), V(k * 0.15, 0.36, 1.32), V(k * 0.04, 0.35, 1.38)], 0.05, 0.01, 7, 5, 0.6), ink, mj);
    for (let t = 0; t < 3; t++) {
      const th = M(new THREE.ConeGeometry(0.014, 0.05, 4), tooth, mj);
      const z = 1.18 + t * 0.06;
      th.position.set(k * (0.16 - t * 0.035), 0.36, z);
      th.rotation.set(Math.PI / 2, 0, k * (1.1 + t * 0.2));
    }
  }

  // Antennae: thin, elbowed, ending in a fan of three leaves.
  for (const k of [-1, 1]) {
    const aj = antJ[k < 0 ? 0 : 1]!.abs;
    M(taper([V(k * 0.17, 0.52, 1.02), V(k * 0.26, 0.6, 1.1), V(k * 0.34, 0.6, 1.2), V(k * 0.4, 0.64, 1.27)], 0.014, 0.01, 4, 4), dull, aj);
    for (let f = 0; f < 3; f++) {
      const lf = M(new THREE.SphereGeometry(0.04, 5, 3), sheenV, aj);
      lf.position.set(k * (0.42 + f * 0.012), 0.64 + (f - 1) * 0.025, 1.3);
      lf.scale.set(0.3, 0.45, 1);
      lf.rotation.set(0, k * 0.5, (f - 1) * 0.5);
    }
  }

  // The acid glob, curled up in the mouth until it is spat.
  const sp = spitJ.abs;
  const glob = M(new THREE.SphereGeometry(0.05, 6, 4), acid, sp);
  glob.position.set(0, 0.4, 1.04);
  glob.scale.set(1, 0.9, 1.4);
  glowSprite(sp, ACID, 0.22, V(0, 0.4, 1.06), 0.5);

  // ---------- Legs ----------
  const legBones: Record<string, THREE.Object3D> = {};
  for (let i = 0; i < 6; i++) {
    const { side, row } = legOf(i);
    const [h0, k0, f0] = LEG[row]!;
    const hip = V(side * h0.x, h0.y, h0.z), knee = V(side * k0.x, k0.y, k0.z), foot = V(side * f0.x, f0.y, f0.z);
    const yaw = Math.atan2(knee.x - hip.x, knee.z - hip.z);
    const th = yjoint(bd, hip, yaw, `leg${i}`);
    const sh = yjoint(th.abs, knee, yaw, `knee${i}`);
    legBones[`leg${i}`] = th.p;
    legBones[`knee${i}`] = sh.p;
    // Coxa and femur: a flattened club, thick at the knee.
    const midF = hip.clone().lerp(knee, 0.5).add(V(0, 0.05, 0));
    M(taper([hip, midF, knee], 0.07, 0.075, 4, 5, 0.6), ink, th.abs);
    // Tibia: angled down to the ground, toothed along its outer edge.
    const out = V(foot.x - knee.x, 0, foot.z - knee.z).normalize();
    const midT = knee.clone().lerp(foot, 0.45).addScaledVector(out, 0.04).add(V(0, 0.03, 0));
    M(taper([knee, midT, foot], 0.065, 0.03, 4, 4, 0.55), ink, sh.abs);
    for (let s = 0; s < 2; s++) {
      const q = knee.clone().lerp(foot, 0.35 + s * 0.3).addScaledVector(out, 0.04);
      const spur = M(new THREE.ConeGeometry(0.02, 0.1, 4), sheenT, sh.abs);
      spur.position.copy(q);
      spur.quaternion.setFromUnitVectors(UP, out.clone().add(V(0, 0.6, 0)).normalize());
    }
    // Tarsus: jointed beads along the ground ending in a hooked claw.
    const toe = foot.clone().addScaledVector(out, 0.16).setY(0.02);
    M(taper([foot, foot.clone().lerp(toe, 0.5).add(V(0, 0.01, 0)), toe], 0.018, 0.01, 2, 3), dull, sh.abs);
    const claw = M(new THREE.ConeGeometry(0.012, 0.07, 3), tooth, sh.abs);
    claw.position.copy(toe);
    claw.quaternion.setFromUnitVectors(UP, out.clone().add(V(0, -0.5, 0)).normalize());
  }

  // Ink seeping off the seams of the shell, like every nightmare; more from the wound when it is cracked.
  const ribs: Ribbon[] = [];
  const wm = basic('#2b2244', 0.42);
  const SMOKE: [THREE.Object3D, number, number, number][] = cracked
    ? [[ab, 0.05, 0.75, -0.35], [ab, -0.25, 0.7, -0.7], [bd, 0.0, 0.82, 0.4]]
    : [[shellJ[0]!.abs, -0.02, 0.9, -0.3], [shellJ[1]!.abs, 0.02, 0.85, -0.75], [bd, 0.0, 0.82, 0.4]];
  for (const [par, x, y, z] of SMOKE) ribs.push(new Ribbon(par, wisp(x, z, 0.55, 4).map((p) => p.add(V(0, y, 0))), 0.026, wm, rng, 0.05));
  glowSprite(bd, '#6a3a9a', 1.8, V(0, 0.75, -0.2), 0.1);
  const throat = glowSprite(hd, ACID, 0.55, V(0, 0.42, 1.06), 0.3, true);

  const t = (n: number): void => {
    for (const r of ribs) r.step();
    throat.material.opacity = 0.18 + ((n * 5) % 7) / 35;
  };
  t(0);
  return done(c, (cracked ? 1.0 : 1.05) * SIZE, t, undefined, bonesOf(c, {
    body: bodyJ.p, abdomen: abdJ.p, shellL: shellJ[0]!.p, shellR: shellJ[1]!.p,
    head: headJ.p, mandL: mandJ[0]!.p, mandR: mandJ[1]!.p, antL: antJ[0]!.p, antR: antJ[1]!.p, spit: spitJ.p,
    ...legBones,
  }));
};
